/* SemBot · le metriche con soglia.
 *
 * Qui vive la logica che trasforma un numero in { valore, colore, verdetto,
 * qualificatore }. Sta in un solo posto perché la regola 4 di STATO.md è che
 * quei quattro pezzi viaggiano SEMPRE insieme: se il calcolo del colore
 * fosse in un componente e il verdetto in un altro, prima o poi una schermata
 * ne mostrerebbe solo una parte.
 */

import { num, pct } from './formato';

/** Drawdown obiettivo della normalizzazione. Va sempre scritto accanto al numero. */
export const DRAWDOWN_OBIETTIVO = 35;

/**
 * A PARI RISCHIO — riporta un rendimento composto a un drawdown obiettivo
 * comune, per deleveraggio: k = obiettivo / |DD|, adj = (1 + r)^k − 1.
 *
 * Il `Math.min(1, …)` è essenziale e non è una difesa: sotto il drawdown
 * obiettivo non c'è nulla da normalizzare, quindi si scala solo verso il basso
 * e il valore resta pari al profitto. Senza di esso una configurazione più
 * prudente del target verrebbe "levereggiata" per finta e sembrerebbe migliore
 * di quanto sia.
 *
 * Identica a quella del motore, del database e del frontend attuale.
 * Verificata su righe di produzione:
 *   143.913,05% @ 44,59% → 30.039%
 *    81.168,17% @ 58,88% →  5.267%
 *       626,26% @ 29,80% →    626%   ← il controllo che dimostra il Math.min
 */
export function aPariRischio(
  profitto: number,
  drawdown: number,
  obiettivo: number = DRAWDOWN_OBIETTIVO,
): number {
  return ((1 + profitto / 100) ** Math.min(1, obiettivo / Math.abs(drawdown)) - 1) * 100;
}

/* --------------------------------------------------------------------------
 * Tono: il significato del colore, non il colore.
 * I componenti traducono il tono in una variabile CSS. Così una schermata non
 * può scegliersi il verde per un motivo che non sia il guadagno.
 * ------------------------------------------------------------------------ */
export type Tono = 'guadagno' | 'perdita' | 'cautela' | 'neutro';

/** Il testo che qualifica l'affidabilità del numero. Mai in un tooltip. */
export interface Qualificatore {
  testo: string;
  /** `true` quando il qualificatore stesso è un avvertimento (va in ambra). */
  fragile: boolean;
}

export interface LetturaSoglia {
  /** Il numero già formattato, o 'n/d' quando manca. */
  valore: string;
  /** Il numero grezzo, `null` se non calcolabile. */
  grezzo: number | null;
  tono: Tono;
  /** La frase che dice cosa SIGNIFICA il valore, non cos'è la metrica. */
  verdetto: string;
  /** La banda di soglia in cui cade, per l'etichetta accanto al titolo. */
  banda: string;
  qualificatore: Qualificatore;
}

/* --------------------------------------------------------------------------
 * TENUTA
 * Rendimento annualizzato del periodo recente diviso quello precedente.
 * `null` quando la storia è troppo corta: mai zero, perché uno zero si legge
 * come un giudizio invece che come un dato mancante.
 * ------------------------------------------------------------------------ */

/** Come è stato diviso il periodo: sono due affidabilità diverse e va detto. */
export type DivisionePeriodo = 'halving' | 'meta';

export function leggiTenuta(
  valore: number | null | undefined,
  divisione: DivisionePeriodo,
): LetturaSoglia {
  const qualificatore: Qualificatore =
    divisione === 'halving'
      ? { testo: 'diviso per cicli di halving', fragile: false }
      : { testo: 'diviso a metà del periodo — meno affidabile', fragile: true };

  if (valore == null || !Number.isFinite(valore)) {
    return {
      valore: 'n/d',
      grezzo: null,
      tono: 'neutro',
      verdetto: 'Serve almeno un biennio di storia',
      banda: 'n/d',
      qualificatore,
    };
  }

  // `walkForwardScore` arriva dal server come RAPPORTO, non come percentuale: 1 significa
  // «rende quanto prima», 0,5 «meta'». Le soglie qui sotto sono in percentuale, quindi la
  // conversione va fatta prima di confrontarle -- senza, un valore di 1 si mostrava come «1%»
  // e finiva nella banda rossa «il grosso era nel passato», che e' il giudizio opposto a
  // quello vero.
  const percentuale = valore * 100;
  const testo = `${num(percentuale)}%`;

  if (percentuale >= 80) {
    return {
      valore: testo,
      grezzo: percentuale,
      tono: 'guadagno',
      verdetto: 'Rende quanto prima',
      banda: '≥ 80%',
      qualificatore,
    };
  }
  if (percentuale >= 40) {
    return {
      valore: testo,
      grezzo: percentuale,
      tono: 'cautela',
      verdetto: 'Tiene, indebolita',
      banda: '40–80%',
      qualificatore,
    };
  }
  return {
    valore: testo,
    grezzo: percentuale,
    tono: 'perdita',
    verdetto: 'Il grosso era nel passato',
    banda: '< 40%',
    qualificatore,
  };
}

/* --------------------------------------------------------------------------
 * NEI RIBASSI
 * Rendimento medio negli anni in cui il MERCATO ha perso. Sotto i 2 anni
 * negativi il numero è fragile: si mostra attenuato, non si nasconde.
 * ------------------------------------------------------------------------ */

export function leggiNeiRibassi(
  valore: number | null | undefined,
  anniNegativi: number | null | undefined,
): LetturaSoglia {
  /* `anniNegativi` nullo significa NON DICHIARATO, non zero. Schiacciarlo a
     zero faceva scrivere «media su 0 anni negativi» accanto a una media che
     esiste: un conteggio inventato, e per giunta in contraddizione col numero
     che qualifica. Il conteggio ignoto è fragile quanto un conteggio basso,
     ma per una ragione diversa e va detta. */
  const qualificatore: Qualificatore =
    anniNegativi == null
      ? {
          testo: 'non è dichiarato su quanti anni negativi è la media — numero fragile',
          fragile: true,
        }
      : anniNegativi < 2
        ? {
            testo: `media su ${anniNegativi} ${anniNegativi === 1 ? 'solo anno negativo' : 'anni negativi'} — numero fragile`,
            fragile: true,
          }
        : { testo: `media su ${anniNegativi} anni in cui il mercato ha perso`, fragile: false };

  if (valore == null || !Number.isFinite(valore)) {
    return {
      valore: 'n/d',
      grezzo: null,
      tono: 'neutro',
      /* «Nessun anno negativo» è una conclusione, e va detta solo quando il
         conteggio la sostiene. Con anni negativi dichiarati e media assente la
         verità è un'altra — la media non c'è — e spacciarla per "non ce n'è
         stato bisogno" farebbe leggere come rassicurante un dato che manca. */
      verdetto:
        anniNegativi != null && anniNegativi > 0
          ? 'Media non calcolata, pur essendoci anni negativi'
          : anniNegativi === 0
            ? 'Nessun anno negativo nel periodo'
            : 'Non misurata su questo periodo',
      banda: 'n/d',
      qualificatore,
    };
  }

  const testo = pct(valore);

  return {
    valore: testo,
    grezzo: valore,
    tono: valore >= 0 ? 'guadagno' : 'perdita',
    verdetto:
      valore >= 0 ? 'Guadagna mentre il mercato perde' : 'Perde anche quando il mercato perde',
    banda: valore >= 0 ? 'positivo' : 'negativo',
    qualificatore,
  };
}

/* --------------------------------------------------------------------------
 * INTORNO
 * Quanto del risultato resta in piedi se UN parametro sbaglia di una tacca:
 * il caso peggiore fra i vicini nella griglia. `null` fuori da un gruppo,
 * perché un backtest singolo non ha vicini con cui confrontarsi.
 *
 * Perché è una lettura con soglia e non un numero grigio: su una griglia è
 * l'unica colonna che distingue un altopiano da un picco. Il commento
 * dell'entity lo dice senza mezzi termini — «100 = spostandosi non si perde
 * nulla; 10 = nove decimi del risultato dipendevano dall'aver azzeccato un
 * valore preciso» — col caso XRP a quattro ore, dove le dodici configurazioni
 * più redditizie tengono fra il 7% e il 29%. Un "12" grigio accanto a
 * "+62.877%" è esattamente la trappola che la regola 4 esiste per evitare.
 *
 * PERCHÉ 70 E 30, E NON 80 E 40.
 * Le due schermate che mostravano l'intorno ne avevano scritte due versioni:
 * una con 80/40, l'altra con 70/30. Un intorno del 35% risultava «pendio» in
 * una e «picco» nell'altra — la stessa riga con due verdetti opposti dentro la
 * stessa applicazione.
 *
 * Vincono 70 e 30. L'80/40 veniva dalla TENUTA, copiato per somiglianza: le
 * due metriche si assomigliano perché sono entrambe una percentuale di
 * risultato che sopravvive, ma non misurano la stessa cosa. La tenuta
 * confronta due METÀ DEL TEMPO — se nella seconda rendi il 35% della prima,
 * il grosso era nel passato. L'intorno confronta i VICINI DI PARAMETRO — se
 * spostando una tacca conservi il 35%, non stai su un picco isolato, stai su
 * un pendio. Applicare la scala della tenuta all'intorno è stato un
 * trasferimento per analogia, non una misura.
 *
 * 70 e 30 sono invece le soglie del frontend in produzione
 * (`sem-bot-ui/src/containers/BacktestSearchContainer.js`, colonna «Intorno»),
 * coi loro tre giudizi già scritti: sono quelle su cui tutte le decisioni sono
 * state prese finora, e cambiarle in silenzio riclassificherebbe
 * configurazioni già valutate.
 * ------------------------------------------------------------------------ */

/** Le bande dell'intorno, in un posto solo perché non ne rinascano altre. */
export const INTORNO_ALTOPIANO = 70;
export const INTORNO_PENDIO = 30;

export function leggiIntorno(
  valore: number | null | undefined,
  /** Il parametro su cui il risultato è più fragile. */
  parametroPiuFragile: string | null | undefined,
  /** Su quanti parametri il minimo è stato cercato. */
  assi: number | null | undefined,
): LetturaSoglia {
  /* Il qualificatore obbligatorio è duplice: SU COSA cade il minimo (il
     parametro più fragile) e su QUANTI assi è stato cercato. Con meno di due
     assi il «caso peggiore fra tutti i parametri» è il caso peggiore fra uno,
     cioè non è un minimo: va detto, non attenuato in silenzio. */
  const pochiAssi = assi == null || assi < 2;
  const pezzi: string[] = [
    assi == null
      ? 'nessun asse dichiarato'
      : `peggior caso spostando un parametro di una tacca, su ${num(assi)} ${assi === 1 ? 'asse misurato' : 'assi misurati'}`,
  ];
  if (parametroPiuFragile) pezzi.push(`il più fragile è ${parametroPiuFragile}`);

  const qualificatore: Qualificatore = {
    testo:
      pezzi.join(' · ') +
      (pochiAssi ? ' — sotto i due assi il peggiore fra tutti è il peggiore fra uno' : ''),
    fragile: pochiAssi,
  };

  if (valore == null || !Number.isFinite(valore)) {
    return {
      valore: 'n/d',
      grezzo: null,
      tono: 'neutro',
      verdetto: 'Fuori griglia: non ha vicini da misurare',
      banda: 'n/d',
      /* Qui il qualificatore non è un avvertimento sul numero: il numero non
         c'è. Segnarlo fragile lo colorerebbe di ambra come se fosse un dato
         dubbio invece che un dato assente. */
      qualificatore: { testo: 'misurabile solo dentro un gruppo', fragile: false },
    };
  }

  const testo = `${num(valore)}%`;

  if (valore >= INTORNO_ALTOPIANO) {
    return {
      valore: testo,
      grezzo: valore,
      tono: 'guadagno',
      verdetto: 'Altopiano: sposta i parametri e regge',
      banda: `≥ ${INTORNO_ALTOPIANO}%`,
      qualificatore,
    };
  }
  if (valore >= INTORNO_PENDIO) {
    return {
      valore: testo,
      grezzo: valore,
      tono: 'cautela',
      verdetto: 'Pendio: perde parecchio se i parametri si spostano',
      banda: `${INTORNO_PENDIO}–${INTORNO_ALTOPIANO}%`,
      qualificatore,
    };
  }
  return {
    valore: testo,
    grezzo: valore,
    tono: 'perdita',
    verdetto: 'Picco: il risultato dipende da valori azzeccati',
    banda: `< ${INTORNO_PENDIO}%`,
    qualificatore,
  };
}

/* --------------------------------------------------------------------------
 * A PARI RISCHIO come lettura con soglia
 * Non ha bande di colore: il suo qualificatore obbligatorio è il drawdown
 * obiettivo, che cambiando cambierebbe la classifica.
 * ------------------------------------------------------------------------ */

export function leggiAPariRischio(
  profitto: number | null | undefined,
  drawdown: number | null | undefined,
  obiettivo: number = DRAWDOWN_OBIETTIVO,
): LetturaSoglia {
  const qualificatore: Qualificatore = {
    testo: `normalizzata a drawdown ${obiettivo}%`,
    fragile: false,
  };

  if (
    profitto == null ||
    drawdown == null ||
    !Number.isFinite(profitto) ||
    !Number.isFinite(drawdown) ||
    drawdown === 0
  ) {
    return {
      valore: 'n/d',
      grezzo: null,
      tono: 'neutro',
      verdetto: 'Serve profitto e drawdown per normalizzare',
      banda: 'n/d',
      qualificatore,
    };
  }

  const v = aPariRischio(profitto, drawdown, obiettivo);
  const sottoObiettivo = Math.abs(drawdown) <= obiettivo;

  return {
    /* Senza segno più: qui il numero è un valore normalizzato, non una
       variazione, e un "+" lo farebbe leggere come un guadagno aggiuntivo. */
    valore: `${num(v)}%`,
    grezzo: v,
    tono: 'neutro',
    verdetto: sottoObiettivo
      ? 'Rischia meno dell’obiettivo: niente da normalizzare'
      : 'Riportata allo stesso rischio delle altre',
    banda: `obiettivo ${obiettivo}%`,
    qualificatore,
  };
}

/* --------------------------------------------------------------------------
 * VERDETTO — la strategia batte il mercato, e a che prezzo.
 * ------------------------------------------------------------------------ */

export type EsitoVerdetto = 'batte' | 'pari' | 'sotto' | 'perde';

export interface Verdetto {
  esito: EsitoVerdetto;
  etichetta: string;
  tono: Tono;
  /** Frase piena per il dettaglio, es. "Batte il mercato di 125,3 punti". */
  titolo: string;
  /** Scarto in punti percentuali rispetto al mercato. */
  scarto: number;
}

/** Sotto questo scarto in punti, strategia e mercato si dichiarano pari. */
const SOGLIA_PARI = 5;

export function calcolaVerdetto(
  profittoStrategia: number,
  profittoMercato: number,
): Verdetto {
  const scarto = profittoStrategia - profittoMercato;
  const punti = `${num(Math.abs(scarto), 1)} punti`;

  if (profittoStrategia < 0) {
    return {
      esito: 'perde',
      etichetta: 'PERDE',
      tono: 'perdita',
      titolo:
        profittoMercato > 0 ? 'Perde, e il mercato saliva' : 'Perde, come il mercato',
      scarto,
    };
  }
  if (scarto > SOGLIA_PARI) {
    return {
      esito: 'batte',
      etichetta: 'BATTE',
      tono: 'guadagno',
      titolo: `Batte il mercato di ${punti}`,
      scarto,
    };
  }
  if (scarto < -SOGLIA_PARI) {
    return {
      esito: 'sotto',
      etichetta: 'SOTTO',
      tono: 'cautela',
      titolo: `Sotto il mercato di ${punti}`,
      scarto,
    };
  }
  return {
    esito: 'pari',
    etichetta: 'PARI',
    tono: 'neutro',
    titolo: 'In linea col mercato',
    scarto,
  };
}

/* --------------------------------------------------------------------------
 * Metriche derivate.
 * ------------------------------------------------------------------------ */

/* Le firme accettano il nullo come tutte le altre letture di questo file.
   Con `number` secco il compilatore spingerebbe il primo chiamante a scrivere
   `cagr ?? 0` — e `cagr`/`calmar` sono `number | null` nell'API: un ripiego a
   zero produrrebbe un calmar di 0, cioè un giudizio, da un dato mancante. */

/** Calmar: rendimento annualizzato diviso drawdown massimo. */
export function calmar(
  cagr: number | null | undefined,
  drawdown: number | null | undefined,
): number | null {
  if (cagr == null || drawdown == null) return null;
  if (!Number.isFinite(cagr) || !Number.isFinite(drawdown) || drawdown === 0) return null;
  return cagr / Math.abs(drawdown);
}

/** CAGR ricavato dal profitto totale e dagli anni coperti. */
export function cagrDaTotale(
  profitto: number | null | undefined,
  anni: number | null | undefined,
): number | null {
  if (profitto == null || anni == null) return null;
  if (!Number.isFinite(profitto) || !Number.isFinite(anni) || anni <= 0) return null;
  return ((1 + profitto / 100) ** (1 / anni) - 1) * 100;
}

/** Il tono di un rendimento: verde se guadagna, rosso se perde. Nient'altro. */
export function tonoRendimento(v: number | null | undefined): Tono {
  if (v == null || !Number.isFinite(v)) return 'neutro';
  return v >= 0 ? 'guadagno' : 'perdita';
}
